import React, { useState, useRef, useEffect } from 'react';
import { Unit } from '../../types';
import { Trash2, Eye, EyeOff } from 'lucide-react';

export interface GuideLine {
  id: string;
  type: 'horizontal' | 'vertical';
  position: number; // position in canvas pixels
}

interface RulerAndGuidesProps {
  canvasWidth: number;
  canvasHeight: number;
  zoomLevel: number;
  panOffset: { x: number; y: number };
  appUnit: Unit;
  setAppUnit: (unit: Unit) => void;
  guides: GuideLine[];
  onAddGuide: (type: 'horizontal' | 'vertical', position: number) => void;
  onUpdateGuide: (id: string, position: number) => void;
  onRemoveGuide: (id: string) => void;
  onClearGuides: () => void;
  showGuides: boolean;
  onToggleGuides: () => void;
  mouseCanvasPos: { x: number; y: number } | null;
  gridDotSpacing?: number;
  children: React.ReactNode; // The main canvas content
}

export const RulerAndGuides: React.FC<RulerAndGuidesProps> = ({
  canvasWidth,
  canvasHeight,
  zoomLevel,
  panOffset,
  appUnit,
  setAppUnit,
  guides,
  onAddGuide,
  onUpdateGuide,
  onRemoveGuide,
  onClearGuides,
  showGuides,
  onToggleGuides,
  mouseCanvasPos,
  gridDotSpacing = 16,
  children
}) => {
  const [draggingGuide, setDraggingGuide] = useState<{ id?: string; type: 'horizontal' | 'vertical'; initialPos: number } | null>(null);
  const [dragCurrentPos, setDragCurrentPos] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Convert pixels to display unit label
  const pxToUnit = (px: number): number => {
    switch (appUnit) {
      case 'mm': return Math.round((px / 3.779527) * 10) / 10;
      case 'cm': return Math.round((px / 37.79527) * 100) / 100;
      case 'in': return Math.round((px / 96) * 100) / 100;
      case 'pt': return Math.round(px * 0.75);
      default: return Math.round(px);
    }
  };


  // Determine tick intervals in pixels based on unit and zoom
  const getTickIntervals = () => {
    const unitPx = appUnit === 'in' ? 96 : appUnit === 'cm' ? 37.79527 : appUnit === 'mm' ? 3.779527 : appUnit === 'pt' ? 1.333333 : 1;
    const visualUnitSize = unitPx * zoomLevel;

    if (appUnit === 'in') {
      if (visualUnitSize > 400) return { major: 0.125, minor: 0.0625, unitPx }; // eighths
      if (visualUnitSize > 200) return { major: 0.25, minor: 0.125, unitPx };  // quarters
      if (visualUnitSize > 100) return { major: 0.5, minor: 0.25, unitPx };    // halfs
      if (visualUnitSize > 50) return { major: 1, minor: 0.5, unitPx };        // inches
      if (visualUnitSize > 25) return { major: 2, minor: 1, unitPx };
      return { major: 5, minor: 1, unitPx };
    }
    
    if (appUnit === 'cm') {
      if (visualUnitSize > 200) return { major: 0.1, minor: 0.05, unitPx }; // 1mm
      if (visualUnitSize > 100) return { major: 0.5, minor: 0.1, unitPx }; // 5mm
      if (visualUnitSize > 50) return { major: 1, minor: 0.5, unitPx }; // 1cm
      if (visualUnitSize > 25) return { major: 2, minor: 1, unitPx };
      return { major: 5, minor: 1, unitPx };
    }

    if (appUnit === 'mm') {
      if (visualUnitSize > 20) return { major: 1, minor: 0.5, unitPx }; // 1mm
      if (visualUnitSize > 10) return { major: 5, minor: 1, unitPx }; // 5mm
      if (visualUnitSize > 5) return { major: 10, minor: 5, unitPx }; // 10mm
      return { major: 50, minor: 10, unitPx };
    }

    if (visualUnitSize > 2) return { major: 10, minor: 5, unitPx };
    if (visualUnitSize > 1) return { major: 50, minor: 10, unitPx };
    if (visualUnitSize > 0.5) return { major: 100, minor: 50, unitPx };
    return { major: 200, minor: 100, unitPx };
  };

  const { major, minor, unitPx } = getTickIntervals();

  const rulerWidthPx = canvasWidth * zoomLevel;
  const rulerHeightPx = canvasHeight * zoomLevel;

  // Calculate top-left of canvas relative to the workspace container
  // Assuming the workspace is flex items-center justify-center
  const canvasLeft = (containerSize.width - rulerWidthPx) / 2 + panOffset.x;
  const canvasTop = (containerSize.height - rulerHeightPx) / 2 + panOffset.y;

  // Handle creating guide from horizontal ruler (Top)
  const handleTopRulerPointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const startY = Math.round((e.clientY - rect.top - canvasTop) / zoomLevel);

    setDraggingGuide({ type: 'horizontal', initialPos: startY });
    setDragCurrentPos(startY);

    const handlePointerMove = (ev: PointerEvent) => {
      if (!containerRef.current) return;
      const stageRect = containerRef.current.getBoundingClientRect();
      const currentY = Math.round((ev.clientY - stageRect.top - canvasTop) / zoomLevel);
      setDragCurrentPos(currentY);
    };

    const handlePointerUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);

      if (!containerRef.current) return;
      const stageRect = containerRef.current.getBoundingClientRect();
      const finalY = Math.round((ev.clientY - stageRect.top - canvasTop) / zoomLevel);

      if (finalY >= 0 && finalY <= canvasHeight) {
        onAddGuide('horizontal', finalY);
      }
      setDraggingGuide(null);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  // Handle creating guide from vertical ruler (Left)
  const handleLeftRulerPointerDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const startX = Math.round((e.clientX - rect.left - canvasLeft) / zoomLevel);

    setDraggingGuide({ type: 'vertical', initialPos: startX });
    setDragCurrentPos(startX);

    const handlePointerMove = (ev: PointerEvent) => {
      if (!containerRef.current) return;
      const stageRect = containerRef.current.getBoundingClientRect();
      const currentX = Math.round((ev.clientX - stageRect.left - canvasLeft) / zoomLevel);
      setDragCurrentPos(currentX);
    };

    const handlePointerUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);

      if (!containerRef.current) return;
      const stageRect = containerRef.current.getBoundingClientRect();
      const finalX = Math.round((ev.clientX - stageRect.left - canvasLeft) / zoomLevel);

      if (finalX >= 0 && finalX <= canvasWidth) {
        onAddGuide('vertical', finalX);
      }
      setDraggingGuide(null);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  // Handle moving existing guide line
  const handleGuideLinePointerDown = (e: React.PointerEvent, guide: GuideLine) => {
    e.stopPropagation();
    e.preventDefault();

    const handlePointerMove = (ev: PointerEvent) => {
      if (!containerRef.current) return;
      const stageRect = containerRef.current.getBoundingClientRect();
      if (guide.type === 'horizontal') {
        const newY = Math.round((ev.clientY - stageRect.top - canvasTop) / zoomLevel);
        onUpdateGuide(guide.id, newY);
      } else {
        const newX = Math.round((ev.clientX - stageRect.left - canvasLeft) / zoomLevel);
        onUpdateGuide(guide.id, newX);
      }
    };

    const handlePointerUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);

      if (!containerRef.current) return;
      const stageRect = containerRef.current.getBoundingClientRect();
      if (guide.type === 'horizontal') {
        const finalY = Math.round((ev.clientY - stageRect.top - canvasTop) / zoomLevel);
        if (finalY < -20 || finalY > canvasHeight + 20) {
          onRemoveGuide(guide.id);
        }
      } else {
        const finalX = Math.round((ev.clientX - stageRect.left - canvasLeft) / zoomLevel);
        if (finalX < -20 || finalX > canvasWidth + 20) {
          onRemoveGuide(guide.id);
        }
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  // Render Horizontal Ruler Ticks
  const renderTopRulerTicks = () => {
    const ticks = [];
    const totalUnits = canvasWidth / unitPx;
    
    for (let u = 0; u <= totalUnits; u += minor) {
      const posPx = u * unitPx;
      const isMajor = Math.abs((u % major) / major) < 0.001 || Math.abs((u % major) / major) > 0.999;
      
      ticks.push(
        <div
          key={`top-tick-${u}`}
          className="absolute top-0 flex flex-col items-center pointer-events-none"
          style={{ left: `${posPx * zoomLevel}px` }}
        >
          <div className={`w-[1px] bg-zinc-500 ${isMajor ? 'h-3' : 'h-1.5'}`} />
          {isMajor && (
            <span className="text-[9px] font-mono text-zinc-400 select-none leading-none mt-0.5">
              {Math.round(u * 100) / 100}
            </span>
          )}
        </div>
      );
    }
    return ticks;
  };

  // Render Vertical Ruler Ticks
  const renderLeftRulerTicks = () => {
    const ticks = [];
    const totalUnits = canvasHeight / unitPx;
    
    for (let u = 0; u <= totalUnits; u += minor) {
      const posPx = u * unitPx;
      const isMajor = Math.abs((u % major) / major) < 0.001 || Math.abs((u % major) / major) > 0.999;
      
      ticks.push(
        <div
          key={`left-tick-${u}`}
          className="absolute left-0 flex items-center pointer-events-none"
          style={{ top: `${posPx * zoomLevel}px` }}
        >
          <div className={`h-[1px] bg-zinc-500 ${isMajor ? 'w-3' : 'w-1.5'}`} />
          {isMajor && (
            <span className="text-[8px] font-mono text-zinc-400 select-none leading-none ml-1 transform -rotate-90 origin-left">
              {Math.round(u * 100) / 100}
            </span>
          )}
        </div>
      );
    }
    return ticks;
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 w-full relative select-none isolate">
      {/* Main 2D Grid Ruler & Stage Container */}
      <div className="grid grid-cols-[24px_auto] grid-rows-[24px_auto] flex-1 overflow-hidden">
        {/* Top-Left Unit Corner Box */}
        <div 
          className="w-full h-full bg-zinc-900 border-r border-b border-zinc-800 flex items-center justify-center relative hover:bg-zinc-800 transition-colors z-20"
          title="Change Unit System"
        >
          <select 
            value={appUnit}
            onChange={(e) => setAppUnit(e.target.value as Unit)}
            className="w-full h-full absolute inset-0 opacity-0 cursor-pointer"
          >
            <option value="mm">mm</option>
            <option value="cm">cm</option>
            <option value="in">inch</option>
            <option value="pt">points</option>
          </select>
          <span className="text-[9px] font-mono text-cyan-400 font-bold pointer-events-none">{appUnit.toUpperCase()}</span>
        </div>

        {/* Top Horizontal Ruler */}
        <div
          onPointerDown={handleTopRulerPointerDown}
          className="w-full h-full bg-zinc-900 border-b border-zinc-800 relative cursor-row-resize overflow-hidden z-10"
          title="Click and drag down to pull a Horizontal Guide"
        >
          <div className="absolute top-0 left-0 h-full" style={{ transform: `translateX(${canvasLeft}px)`, width: `${rulerWidthPx}px` }}>
            {renderTopRulerTicks()}
            {/* Active Cursor Tracking Marker */}
            {mouseCanvasPos && (
              <div
                className="absolute top-0 bottom-0 w-[1.5px] bg-cyan-400 z-20 pointer-events-none"
                style={{ left: `${mouseCanvasPos.x * zoomLevel}px` }}
              />
            )}
          </div>
        </div>

        {/* Left Vertical Ruler */}
        <div
          onPointerDown={handleLeftRulerPointerDown}
          className="w-full h-full bg-zinc-900 border-r border-zinc-800 relative cursor-col-resize overflow-hidden z-10"
          title="Click and drag right to pull a Vertical Guide"
        >
          <div className="absolute top-0 left-0 w-full" style={{ transform: `translateY(${canvasTop}px)`, height: `${rulerHeightPx}px` }}>
            {renderLeftRulerTicks()}
            {/* Active Cursor Tracking Marker */}
            {mouseCanvasPos && (
              <div
                className="absolute left-0 right-0 h-[1.5px] bg-cyan-400 z-20 pointer-events-none"
                style={{ top: `${mouseCanvasPos.y * zoomLevel}px` }}
              />
            )}
          </div>
        </div>

        {/* Workspace Container */}
        <div
          ref={containerRef}
          id="canvas-viewport-container"
          className="w-full h-full relative overflow-hidden bg-[radial-gradient(#27272a_1px,transparent_1px)]"
          style={{ backgroundSize: `${gridDotSpacing}px ${gridDotSpacing}px` }}
        >
          {children}

          {/* Interactive Guides Overlay Layer */}
          {showGuides && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden z-20">
              {guides.map((guide) => (
                <div
                  key={guide.id}
                  onPointerDown={(e) => handleGuideLinePointerDown(e, guide)}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    onRemoveGuide(guide.id);
                  }}
                  className={`absolute pointer-events-auto group ${
                    guide.type === 'horizontal'
                      ? 'left-0 right-0 h-1.5 -mt-0.75 cursor-row-resize flex items-center justify-end'
                      : 'top-0 bottom-0 w-1.5 -ml-0.75 cursor-col-resize flex flex-col justify-end'
                  }`}
                  style={
                    guide.type === 'horizontal'
                      ? { top: `${canvasTop + guide.position * zoomLevel}px` }
                      : { left: `${canvasLeft + guide.position * zoomLevel}px` }
                  }
                  title={`Guide at ${pxToUnit(guide.position)} ${appUnit} (Double-click to remove)`}
                >
                  <div
                    className={`${
                      guide.type === 'horizontal'
                        ? 'w-full h-[1px] bg-cyan-400 border-b border-dashed border-cyan-300 shadow-sm group-hover:bg-amber-400 group-hover:h-[2px]'
                        : 'h-full w-[1px] bg-cyan-400 border-r border-dashed border-cyan-300 shadow-sm group-hover:bg-amber-400 group-hover:w-[2px]'
                    }`}
                  />
                  <div className="opacity-0 group-hover:opacity-100 bg-zinc-900 text-cyan-300 text-[9px] font-mono px-1 py-0.5 rounded border border-cyan-500/50 shadow-md transition-opacity">
                    {pxToUnit(guide.position)} {appUnit}
                  </div>
                </div>
              ))}

              {/* Dragging New Guide Live Preview */}
              {draggingGuide && (
                <div
                  className={`absolute pointer-events-none ${
                    draggingGuide.type === 'horizontal'
                      ? 'left-0 right-0 h-[1.5px] bg-amber-400 border-b border-dashed border-amber-300'
                      : 'top-0 bottom-0 w-[1.5px] bg-amber-400 border-r border-dashed border-amber-300'
                  }`}
                  style={
                    draggingGuide.type === 'horizontal'
                      ? { top: `${canvasTop + dragCurrentPos * zoomLevel}px` }
                      : { left: `${canvasLeft + dragCurrentPos * zoomLevel}px` }
                  }
                />
              )}
            </div>
          )}
          
          {/* Floating Guide Controls (Placed cleanly top-right so it never collides with tools or sidebars) */}
          {guides.length > 0 && (
            <div className="absolute top-2.5 right-3 flex items-center gap-1.5 z-20 pointer-events-auto">
              <button
                onClick={onToggleGuides}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] font-medium transition-all shadow-sm ${
                  showGuides ? 'bg-cyan-950/90 text-cyan-300 border-cyan-700/70 hover:bg-cyan-900/90' : 'bg-zinc-900/90 text-zinc-400 border-zinc-700 hover:text-zinc-200 hover:bg-zinc-800/90'
                } backdrop-blur-md cursor-pointer`}
                title="Toggle Guide Lines Visibility"
              >
                {showGuides ? <Eye className="w-3.5 h-3.5 text-cyan-400" /> : <EyeOff className="w-3.5 h-3.5" />}
                <span>Guides ({guides.length})</span>
              </button>
              <button
                onClick={onClearGuides}
                className="flex items-center gap-1 px-2 py-1 text-[11px] bg-zinc-900/90 hover:bg-red-950/80 rounded-md border border-zinc-700 hover:border-red-700/60 text-zinc-400 hover:text-red-300 transition-all backdrop-blur-md cursor-pointer shadow-sm"
                title="Clear All Guides"
              >
                <Trash2 className="w-3.5 h-3.5" /> Clear
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
